import { SITE_URL, ORG_ID } from "@/components/schemaConstants";

export const clinicianFaqs: { question: string; answer: string }[] = [
  {
    question: "Who is a candidate for SAINT®?",
    answer:
      "Adults 22 and older with major depressive disorder (MDD) whose depression has not improved enough on prior antidepressant medication at adequate dose and duration. SAINT® is FDA-cleared for MDD only — not for anxiety, OCD, PTSD, bipolar disorder, or any other condition. Contraindications include non-removable magnetic-sensitive metal in or near the head, certain implanted devices, and increased intracranial pressure. A free consultation confirms candidacy.",
  },
  {
    question: "How fast can my patient be seen?",
    answer:
      "After we receive your referral, our clinical team contacts your patient to schedule a free consultation. Turnaround from referral to the treatment week depends on scheduling and insurance verification, which we handle at no cost. Contact us at (954) 827-9793 for current availability and to discuss urgent referrals.",
  },
  {
    question: "Will I get notes back?",
    answer:
      "Yes. After the five-day treatment week, we send you a summary with the fMRI targeting details, treatment parameters, pre- and post-treatment MADRS scores, and follow-up recommendations. If you are managing medications, we coordinate with you throughout. We treat SAINT® as one step in your patient's overall care, not a handoff.",
  },
  {
    question: "Does NWCOA pay referral fees?",
    answer:
      "No. NWCOA does not pay referral fees, kickbacks, or revenue share to referring providers. We comply with the Florida Patient Brokering Act and all applicable federal and state laws. Referrals are made for the benefit of the patient, and care is coordinated back to the referring provider.",
  },
  {
    question: "What if my patient is not a candidate for SAINT®?",
    answer:
      "If your patient does not meet the criteria for SAINT®, we let you know the reason and recommend an alternative path. NWCOA offers standard TMS, SPRAVATO® (esketamine), ketamine, medication management, and psychotherapy in the same clinic, so your patient is not left without options. We coordinate the alternative back to you.",
  },
];

export const comparisonRows: [string, string, string][] = [
  [
    "Diagnosis",
    "Major depressive disorder (MDD)",
    "Confirmed diagnosis of MDD; screening for bipolar disorder and comorbidities",
  ],
  [
    "Age",
    "Adults 22+ (NWCOA treats adults 22 and older)",
    "Date of birth on referral",
  ],
  [
    "Prior antidepressant trials",
    "Not enough improvement on prior antidepressant medication",
    "List of medications tried, doses, durations, and outcomes",
  ],
  [
    "Contraindications",
    "No non-removable magnetic-sensitive metal near the head; no contraindicated implanted devices; no increased intracranial pressure",
    "Relevant medical history, implanted devices, and head metal screening",
  ],
  [
    "Severity",
    "Treatment-resistant depression (adequate trials without enough relief)",
    "Current PHQ-9 or MADRS score if available",
  ],
  [
    "Stability",
    "Able to attend 10 sessions per day for 5 consecutive days",
    "Confirmation the patient can commit to the 5-day week and has transportation or lodging arranged",
  ],
];

export const tocItems: [string, string][] = [
  ["Evidence Summary", "#evidence-summary"],
  ["Candidacy and Exclusions", "#candidacy-and-exclusions"],
  ["Referral Steps and Turnaround", "#referral-steps"],
  ["Care Coordination", "#care-coordination"],
  ["Peer-to-Peer with Dr. Pietras", "#peer-to-peer"],
  ["One-Pager and Referral Form", "#one-pager"],
  ["FAQ", "#faq"],
  ["Cited Research", "#cited-research"],
];

export const citedResearch: { n: number; text: string; url: string }[] = [
  {
    n: 1,
    text: "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
    url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
  },
  {
    n: 2,
    text: "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716-726.",
    url: "https://pubmed.ncbi.nlm.nih.gov/32463539/",
  },
  {
    n: 3,
    text: "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2021;178(10):898-909.",
    url: "https://pubmed.ncbi.nlm.nih.gov/34256788/",
  },
  {
    n: 4,
    text: "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
    url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
  },
];

export const pageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE_URL}/saint-for-clinicians#webpage`,
      name: "SAINT® for Clinicians: Referral Guide, Candidacy & Outcomes",
      url: `${SITE_URL}/saint-for-clinicians`,
      description:
        "For psychiatrists, therapists and PCPs: SAINT® evidence, candidacy criteria, the referral process, and how we coordinate care back to you.",
      inLanguage: "en",
      lastReviewed: "2026-10",
      reviewedBy: {
        "@type": "Person",
        name: "Daniel Pietras, MD",
        url: `${SITE_URL}/dr-daniel-pietras-md`,
        honorificSuffix: "MD",
        jobTitle: "Medical Director & Psychiatrist",
      },
      about: {
        "@type": "MedicalTherapy",
        name: "SAINT® Neuromodulation System",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "physician",
      },
    },
    {
      "@type": "FAQPage",
      "@id": `${SITE_URL}/saint-for-clinicians#faqpage`,
      mainEntity: clinicianFaqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.answer,
        },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${SITE_URL}/saint-for-clinicians#breadcrumbs`,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: SITE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "SAINT® for Clinicians",
          item: `${SITE_URL}/saint-for-clinicians`,
        },
      ],
    },
    {
      "@type": "MedicalClinic",
      "@id": ORG_ID,
      name: "Neuro Wellness TMS Centers of America",
      url: SITE_URL,
      telephone: "+1-954-827-9793",
      address: {
        "@type": "PostalAddress",
        streetAddress: "9734 W Sample Rd",
        addressLocality: "Coral Springs",
        addressRegion: "FL",
        postalCode: "33065",
        addressCountry: "US",
      },
    },
  ],
};
